<template>
  <div
    class="network-diagram-node"
    @mouseleave="closeHoverAndMenu"
    :data-node-endpoint="nodeEndpoint"
    :data-node-uuid="nodeUUID"
  >
    <div @mouseenter="hoverActive = true">
      <div class="symbol-container">
        <component :is="nodeSymbol" :style="{ width: symbolSize + 'px' }" />
      </div>

      <div
        v-for="item in nodeStructure"
        :key="item.label"
        v-if="nodeData[item.varName] !== null"
        class="normal-data"
      >
        <label v-if="item.label">{{ item.label }}: </label>

        <span style="color: black; font-weight: bold">
          {{ nodeData[item.varName] }}
        </span>
      </div>
    </div>

    <div
      v-if="hoverActive && !zoomIn"
      @mouseleave="closeHoverAndMenu"
      @contextmenu.prevent="openObjectMenu"
      class="network-diagram-hover-container"
    >
      <MoreIcon class="network-diagram-hover-button" />
    </div>
  </div>
</template>

<script>
import OLTIcon from "@/assets/home/tabs/network/OLT.svg";
import FDCIcon from "@/assets/home/tabs/network/FDC.svg";
import ClosureIcon from "@/assets/home/tabs/network/Closure.svg";
import DropClosureIcon from "@/assets/home/tabs/network/DropClosure.svg";
import SDUIcon from "@/assets/home/tabs/network/sdu.svg";
import MDUIcon from "@/assets/home/tabs/network/mdu.svg";
import MoreIcon from "@/assets/home/tabs/network/more.svg";
import OtherIcon from "@/assets/home/tabs/network/Other.svg";

export default {
  name: "NetworkDiagramNode",

  components: {
    OLTIcon,
    FDCIcon,
    ClosureIcon,
    SDUIcon,
    MDUIcon,
    MoreIcon,
    OtherIcon
  },

  props: {
    /**
     * Type of node to be represented.
     * */
    nodeType: {
      type: String,
      required: true
    },

    /**
     * Network diagram data of the selected Technical Local or closure.
     */
    nodeData: {
      type: Object,
      required: true
    },

    /**
     * If the object is being showed in the zoom in window.
     */
    zoomIn: {
      type: Boolean,
      default: false
    }
    /*    nodeStructure: {
      type: Array
    }*/
  },

  data() {
    return {
      /**
       * If the mouse is hovering the node or not
       */
      hoverActive: false,

      /**
       * If the node object menu is open or not.
       */
      menuOpen: false
    };
  },

  computed: {
    /**
     * Structure of the node labelling, if the node is a Technical Local
     */
    tlDataStructure() {
      return [
        { label: this.$i18n.t("ID"), varName: "tl_id" },
        { label: this.$i18n.tc("Label", 1), varName: "tl_label" },
        { label: this.$i18n.tc("Reference", 1), varName: "tl_material_id" },
        {
          label:
            this.$i18n.tc("HP", 2) +
            " (" +
            this.$i18n.t("Total").toLowerCase() +
            ")",
          varName: "tl_hps"
        },
        {
          label:
            this.$i18n.tc("HP", 2) +
            " (" +
            this.$i18n.t("InUse").toLowerCase() +
            ")",
          varName: "tl_hp_in_use"
        },
        {
          label:
            this.$i18n.tc("HP", 2) +
            " (" +
            this.$i18n.t("Rounded").toLowerCase() +
            ")",
          varName: "tl_hps_rounded"
        },
        {
          label:
            this.$i18n.tc("Fiber", 2) +
            " (" +
            this.$i18n.t("Passing").toLowerCase() +
            ")",
          varName: "tl_passing"
        },
        {
          label:
            this.$i18n.tc("Fiber", 2) +
            " (" +
            this.$i18n.t("Spliced").toLowerCase() +
            ")",
          varName: "tl_splices"
        },
        { label: this.$i18n.tc("Splitter", 2), varName: "tl_splitter" }
      ];
    },

    /**
     * Structure of the node labelling, if the node is a Closure
     */
    clDataStructure() {
      return [
        { label: this.$i18n.t("ID"), varName: "cl_id" },
        { label: this.$i18n.tc("Label", 1), varName: "cl_label" },
        { label: this.$i18n.tc("Reference", 1), varName: "cl_material_id" },
        {
          label:
            this.$i18n.tc("HP", 2) +
            " (" +
            this.$i18n.t("Total").toLowerCase() +
            ")",
          varName: "cl_hps"
        },
        {
          label:
            this.$i18n.tc("HP", 2) +
            " (" +
            this.$i18n.t("InUse").toLowerCase() +
            ")",
          varName: "cl_hp_in_use"
        },
        {
          label:
            this.$i18n.tc("HP", 2) +
            " (" +
            this.$i18n.t("Rounded").toLowerCase() +
            ")",
          varName: "cl_hps_rounded"
        },
        {
          label:
            this.$i18n.tc("Fiber", 2) +
            " (" +
            this.$i18n.t("Passing").toLowerCase() +
            ")",
          varName: "cl_passing"
        },
        {
          label:
            this.$i18n.tc("Fiber", 2) +
            " (" +
            this.$i18n.t("Spliced").toLowerCase() +
            ")",
          varName: "cl_splices"
        },
        { label: this.$i18n.t("SplitterCount"), varName: "cl_splitter_count" },
        {
          label: this.$i18n.t("SplitterActiveOutPorts"),
          varName: "cl_splitter_active_out_ports"
        },
        { varName: "tp_physical_type" }
      ];
    },

    /**
     * Determines which should be the node symbol depending on the node type.
     * Returns {component}
     */
    nodeSymbol() {
      switch (this.nodeType) {
        case "OLT":
          return OLTIcon;
        case "FDC":
          return FDCIcon;
        case "DropClosure":
          if (this.nodeData.cl_logical_type === "DN") {
            return DropClosureIcon;
          } else {
            return OtherIcon;
          }
        case "Closure":
          if (this.nodeData.cl_logical_type === "SC") {
            return ClosureIcon;
          } else {
            return OtherIcon;
          }
        case "SDU":
          return SDUIcon;
        case "MDU":
          return MDUIcon;
        default:
          return MDUIcon;
      }
    },

    /**
     * Determines the size of the node symbol depending on the node type.
     * Returns {number}
     */
    symbolSize() {
      switch (this.nodeType) {
        case "OLT":
          return 50;
        case "FDC":
          return 40;
        case "Closure":
        case "DropClosure":
          return 35;
        default:
          return 35;
      }
    },

    /**
     * Determines which should be the labelling structure used depending on the node type.
     * Returns {object}
     * */
    nodeStructure() {
      if (this.nodeData.type === "Closure") return this.clDataStructure;
      else return this.tlDataStructure;
    },

    nodeEndpoint() {
      if (this.nodeData.type === "Closure") {
        return "closures";
      } else if (this.nodeData.type === "TechnicalLocal") {
        return "technicallocals";
      }
      return null;
    },

    nodeUUID() {
      if (this.nodeData.type === "Closure") {
        return this.nodeData.cl_uuid;
      } else if (this.nodeData.type === "TechnicalLocal") {
        return this.nodeData.tl_uuid;
      }
      return null;
    }
  },

  /*  created() {
    this.tlDataStructure = this.$store.getters.getNetworkDiagramTLStructure;
    this.clDataStructure = this.$store.getters.getNetworkDiagramClosureStructure;
  },*/

  methods: {
    /**
     * Closes the menu and makes the hover style disappear.
     */
    closeHoverAndMenu() {
      this.menuOpen = false;
      this.hoverActive = false;
    },

    /**
     * Opens the object menu on top of the selected object.
     */
    openObjectMenu(e) {
      let data = {
        positioningInfo: {
          x: e.x,
          y: e.y
        },
        selectedObject: {
          type: "node",
          nodeType: this.nodeType,
          objectData: this.nodeData
        }
      };
      this.$store.dispatch("OPEN_NETWORK_DIAGRAM_OBJECT_MENU", data);
    }
  }
};
</script>

<style scoped>
.network-diagram-node {
  position: relative;
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 5px;
}

.network-diagram-node .symbol-container {
  height: 50px;
  width: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.normal-data {
  font-size: 9px;
  opacity: 0.83;
  text-align: left;
  word-wrap: break-word;
  max-width: 100px;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Content/Tabs/Network/NetworkDiagram/NetworkDiagramNode.vue